
import {Avatar, Button, Chip, Divider, ListItem, Paper, TextField} from "@mui/material";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {capitalizeFirstLetter} from "../Helpers/TextStyling";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import React, {useContext, useEffect, useState} from "react";
import usePA_GlobalStore from "../../Services/ZustandGlobalStore";
import dayjs from "dayjs";
import {PAContext} from "../../Services/PAContext";
import {Tooltip} from "recharts";


function UnifiedBudgetBusinessExpenseReport() {

    const { PA_GlobalObject } = usePA_GlobalStore();

    const { dataArray, sendSocketMessage, removeFirstBusinessExpenseReportActions } = useContext(PAContext);
    const { BusinessExpenseReportPipeline } = dataArray;

    const attendeesWithExpenses = PA_GlobalObject.Planning.attendees.filter((attendee) =>
        [
            PA_GlobalObject.Planning.lodging,
            PA_GlobalObject.Planning.meals,
            PA_GlobalObject.Planning.activities,
            PA_GlobalObject.Planning.flights,
        ].some((category) =>
            category.some((item) =>
                item.attendees?.some((att) => att.id === attendee.id && att.is_business_expense)
            )
        ) ||
        PA_GlobalObject.Planning.receipts.some(
            (receipt) => receipt.attendee_id === attendee.id && receipt.is_business_expense
        )
    );



    const [eventTotalLimit, setEventTotalLimit] = useState(0);
    const [eventDailyLimit, setEventDailyLimit] = useState(0);
    const [expenseCurrency, setExpenseCurrency] = useState('USD');

    // **Category-Based Limits**
    const [categoryMode, setCategoryMode] = useState("same"); // "same" or "custom"

    const [useLodgingLimit, setUseLodgingLimit] = useState(false);
    const [lodgingLimit, setLodgingLimit] = useState("");

    const [useFlightLimit, setUseFlightLimit] = useState(false);
    const [flightLimit, setFlightLimit] = useState("");

    const [useActivityLimit, setUseActivityLimit] = useState(false);
    const [activityLimit, setActivityLimit] = useState("");

    const [useMealLimit, setUseMealLimit] = useState(false);
    const [mealLimit, setMealLimit] = useState("");

    const [useSuppliesLimit, setUseSuppliesLimit] = useState(false);
    const [suppliesLimit, setSuppliesLimit] = useState("");

    const [useIncidentalsLimit, setUseIncidentalsLimit] = useState(false);
    const [incidentalsLimit, setIncidentalsLimit] = useState("");

    const [useShoppingLimit, setUseShoppingLimit] = useState(false);
    const [shoppingLimit, setShoppingLimit] = useState("");

    const [useTransportationLimit, setUseTransportationLimit] = useState(false);
    const [transportationLimit, setTransportationLimit] = useState("");

    // Individual Attendee Limits
    const [individualLimits, setIndividualLimits] = useState(
        PA_GlobalObject.Planning.attendees.reduce((acc, attendee) => {
            acc[attendee.id] = {
                lodging: "",
                flights: "",
                activities: "",
                meals: "",
                supplies: "",
                incidentals: "",
                shopping: "",
                transportation: ""
            };
            return acc;
        }, {})
    );


    useEffect(() => {
        const businessAuth = usePA_GlobalStore.getState().PA_GlobalObject.Planning.business_authorizations || {};

        //load all of the limits
        setExpenseCurrency(businessAuth.currency || "USD");
        setEventTotalLimit(businessAuth.eventTotalLimit || 0);
        setEventDailyLimit(businessAuth.dailyLimit || 0);

        // 🌟 Load Category-Based Limits
        setUseLodgingLimit(!!businessAuth['categoryLimits']?.lodging || false);
        setLodgingLimit(businessAuth['categoryLimits']?.lodging || "");

        setUseFlightLimit(!!businessAuth['categoryLimits']?.flights || false);
        setFlightLimit(businessAuth['categoryLimits']?.flights || "");

        setUseActivityLimit(!!businessAuth['categoryLimits']?.activities || false);
        setActivityLimit(businessAuth['categoryLimits']?.activities || "");

        setUseMealLimit(!!businessAuth['categoryLimits']?.meals || false);
        setMealLimit(businessAuth['categoryLimits']?.meals || "");

        setUseSuppliesLimit(!!businessAuth['categoryLimits']?.supplies || false);
        setSuppliesLimit(businessAuth['categoryLimits']?.supplies || "");

        setUseIncidentalsLimit(!!businessAuth['categoryLimits']?.incidentals || false);
        setIncidentalsLimit(businessAuth['categoryLimits']?.incidentals || "");

        setUseShoppingLimit(!!businessAuth['categoryLimits']?.shopping || false);
        setShoppingLimit(businessAuth['categoryLimits']?.shopping || "");

        setUseTransportationLimit(!!businessAuth['categoryLimits']?.transportation || false);
        setTransportationLimit(businessAuth['categoryLimits']?.transportation || "");

        // 🌟 Load Individual Attendee Limits
        if (businessAuth.individualLimits) {
            setIndividualLimits(businessAuth.individualLimits);
        }

    }, [PA_GlobalObject.Planning.business_authorizations]);

    const remainingLimits = {
        eventTotal: eventTotalLimit, // Overall event-wide limit
        dailyTotal: eventDailyLimit,
        categories: {
            lodging: useLodgingLimit ? lodgingLimit : null,
            flights: useFlightLimit ? flightLimit : null,
            activities: useActivityLimit ? activityLimit : null,
            meals: useMealLimit ? mealLimit : null,
            supplies: useSuppliesLimit ? suppliesLimit : null,
            incidentals: useIncidentalsLimit ? incidentalsLimit : null,
            shopping: useShoppingLimit ? shoppingLimit : null,
            transportation: useTransportationLimit ? transportationLimit : null,
        },
        individual: { ...individualLimits } // Copy of per-attendee limits
    };

    const [expandedItems, setExpandedItems] = useState({});

    // Function to toggle expansion
    const toggleExpand = (attendeeId, itemId) => {
        const key = `${attendeeId}-${itemId}`; // Create a unique key
        setExpandedItems((prev) => ({
            ...prev,
            [key]: !prev[key], // Toggle only for this attendee's specific item
        }));
    };

    const formatDate = (timestamp) => (timestamp ? dayjs(timestamp).format("MMM D, YYYY") : "N/A");


    const [reviewTokens, setReviewTokens] = useState(
        PA_GlobalObject?.Planning?.review_access_tokens || []
    );
    const [sendingEmail, setSendingEmail] = useState(false);
    const [revokingToken, setRevokingToken] = useState(false);
    const [postingMessage, setPostingMessage] = useState(false);

    useEffect(() => {
        console.log("reviewTokens triggered : ", reviewTokens);
    }, [reviewTokens]);

    useEffect(() => {
        if (!BusinessExpenseReportPipeline || BusinessExpenseReportPipeline.length === 0) return;

        const _incoming = BusinessExpenseReportPipeline[0];

        const done = () => {
            removeFirstBusinessExpenseReportActions();
        };

        // 1. Resend Review Email Results
        if (_incoming.action === "E_resendExpenseReviewEmailResults") {
            setSendingEmail(false);
            if (_incoming.result === "ok") {
                alert(_incoming.msg || "Review email successfully resent!");
            } else {
                alert(_incoming.reason || "Failed to resend review email.");
            }
            done();
            return;
        }

        // 2. Revoke / Restore Token Results
        if (_incoming.action === "E_revokeExpenseReviewTokenResults") {
            console.log("E_revokeExpenseReviewTokenResults 999999999999999 : ", _incoming);

            setRevokingToken(false);
            if (_incoming.result === "ok") {

                const targetTokenId = _incoming.token_id;
                const isRevoked = _incoming.is_revoked;

                setReviewTokens(prevTokens => {
                    const updated = prevTokens.map(t => {
                        if (t.token === targetTokenId || t.id === targetTokenId) {
                            return { ...t, status: isRevoked ? "REVOKED" : "PENDING" };
                        }
                        return t;
                    });


                    // Keep global object in sync
                    if (PA_GlobalObject?.Planning) {
                        PA_GlobalObject.Planning.review_access_tokens = updated;
                    }
                    return updated; // React automatically re-renders the screen!
                });

                alert(isRevoked ? "Review link has been revoked." : "Review link access restored!");
            } else {
                alert(_incoming.reason || "Failed to update review link status.");
            }
            done();
            return;
        }

        // 3. Add Expense Review Message Results
        if (_incoming.action === "E_addExpenseReviewMessageResults") {
            setPostingMessage(false);
            if (_incoming.result === "ok") {
                setNewMessageText("");

                if (_incoming.new_message) {
                    const targetTokenId = _incoming.token_id;

                    setReviewTokens(prevTokens => {
                        const updated = prevTokens.map(t => {
                            if (t.token === targetTokenId) {
                                return {
                                    ...t,
                                    messages: [...(t.messages || []), _incoming.new_message]
                                };
                            }
                            return t;
                        });

                        // Keep global object in sync
                        if (PA_GlobalObject?.Planning) {
                            PA_GlobalObject.Planning.review_access_tokens = updated;
                        }
                        return updated; // React automatically re-renders the screen!
                    });
                }
            } else {
                alert(_incoming.reason || "Failed to post message.");
            }
            done();
            return;
        }

    }, [BusinessExpenseReportPipeline]);


    // State indexed per attendee
    const [recipientEmails, setRecipientEmails] = useState({});
    const [messageForRecipients, setMessageForRecipients] = useState({});

    // Message thread state (token level)
    const [activeThreadTokenId, setActiveThreadTokenId] = useState(null);
    const [newMessageText, setNewMessageText] = useState("");

    // Action: Submit initial review request
    const handleSendReportForReview = (attendee) => {
        const email = recipientEmails[attendee.id] || "";
        const message = messageForRecipients[attendee.id] || "";

        if (!email || !email.includes("@")) {
            alert("Please enter a valid recipient email address.");
            return;
        }

        if (PA_GlobalObject.Planning?.status !== 10) {
            sendSocketMessage({
                action: "E_sendEventExpenseReport",
                event_id: PA_GlobalObject.Planning.id,
                attendee_id: attendee.id,
                reviewer_email: email,
                message: message
            });
        }

        // Reset inputs for this attendee
        setRecipientEmails((prev) => ({ ...prev, [attendee.id]: "" }));
        setMessageForRecipients((prev) => ({ ...prev, [attendee.id]: "" }));
    };

    // Action: Resend review email
    const handleResendReviewEmail = (tokenObj) => {
        console.log("handleResendReviewEmail : ", tokenObj);
        sendSocketMessage({
            action: "E_resendExpenseReviewEmail",
            event_id: PA_GlobalObject.Planning.id,
            token_id: tokenObj.token,
            reviewer_email: tokenObj.reviewer_email
        });
    };

    // Action: Toggle token revocation (Revoke / Restore access)
    const handleRevokeToken = (tokenObj) => {

        console.log("handleRevokeToken : ", tokenObj);

        // Determine whether we are revoking or restoring based on current status
        const isRevoked = tokenObj?.status === "REVOKED";

        const confirmMessage = isRevoked
            ? "Are you sure you want to restore access for this review request? The reviewer will be able to access this link again."
            : "Are you sure you want to revoke access for this review request? The reviewer will no longer be able to use this link.";

        if (window.confirm(confirmMessage)) {
            setRevokingToken(true);
            sendSocketMessage({
                action: "E_revokeExpenseReviewToken",
                event_id: PA_GlobalObject.Planning.id,
                token_id: tokenObj.token || tokenObj.id
            });
        }
    };
    // Action: Post comment to thread
    const handleAddMessage = (tokenId) => {
        if (!newMessageText.trim()) return;

        sendSocketMessage({
            action: "E_addExpenseReviewMessage",
            event_id: PA_GlobalObject.Planning.id,
            token_id: tokenId,
            message: newMessageText,
            sender: "ATTENDEE"
        });

        setNewMessageText("");
    };

    // Status Badge Helper
    const renderStatusBadge = (status) => {
        const statusUpper = (status || "PENDING").toUpperCase();
        const styleMap = {
            PENDING: { bg: "#fef3c7", color: "#d97706", label: "Pending" },
            APPROVED: { bg: "#dcfce7", color: "#15803d", label: "Approved" },
            REJECTED: { bg: "#fee2e2", color: "#b91c1c", label: "Rejected" },
            REVOKED: { bg: "#f3f4f6", color: "#4b5563", label: "Revoked" },
            EXPIRED: { bg: "#f3f4f6", color: "#6b7280", label: "Expired" }
        };
        const current = styleMap[statusUpper] || styleMap.PENDING;

        return (
            <Box
                component="span"
                sx={{
                    px: 1.2,
                    py: 0.3,
                    borderRadius: "12px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    backgroundColor: current.bg,
                    color: current.color,
                    display: "inline-block"
                }}
            >
                {current.label}
            </Box>
        );
    };




    return (
        <Box>
            <Box>
                {attendeesWithExpenses.length === 0 ? (
                    <Typography>No business expenses found.</Typography>
                ) : (
                    attendeesWithExpenses.map((attendee) => {

                        console.log("attendeesWithExpenses attendee: ", attendee);

                        const attendeeExpenses = {
                            lodging: PA_GlobalObject.Planning.lodging.filter((lodgingItem) =>
                                lodgingItem.attendees?.some(
                                    (att) => att.id === attendee.id && att.is_business_expense
                                )
                            ),
                            meals: PA_GlobalObject.Planning.meals.filter((mealItem) =>
                                mealItem.attendees?.some(
                                    (att) => att.id === attendee.id && att.is_business_expense
                                )
                            ),
                            activities: PA_GlobalObject.Planning.activities.filter((activityItem) =>
                                activityItem.attendees?.some(
                                    (att) => att.id === attendee.id && att.is_business_expense
                                )
                            ),
                            flights: PA_GlobalObject.Planning.flights.filter((flightItem) =>
                                flightItem.attendees?.some(
                                    (att) => att.id === attendee.id && att.is_business_expense
                                )
                            ),
                            supplies: PA_GlobalObject.Planning.receipts.filter((receiptsItem) =>
                                receiptsItem.section.toLowerCase() === "supplies" && receiptsItem.attendee_id === attendee.id
                            ),
                            incidentals: PA_GlobalObject.Planning.receipts.filter((receiptsItem) =>
                                receiptsItem.section.toLowerCase() === "incidentals" && receiptsItem.attendee_id === attendee.id
                            ),
                            shopping: PA_GlobalObject.Planning.receipts.filter((receiptsItem) =>
                                receiptsItem.section.toLowerCase() === "shopping" && receiptsItem.attendee_id === attendee.id
                            ),
                            transportation: PA_GlobalObject.Planning.receipts.filter((receiptsItem) =>
                                receiptsItem.section.toLowerCase() === "transportation" && receiptsItem.attendee_id === attendee.id
                            ),
                        };

                        // Calculate the total for the attendee grouped by currency
                        const totalExpensesByCurrency = {};
                        Object.values(attendeeExpenses).forEach((category) => {

                            category.forEach((item) => {
                                let expenseAmount = 0;
                                const currency = item.currency || "USD"; // Fallback to USD

                                if (Array.isArray(item.attendees)) {
                                    // For lodging, meals, activities, flights
                                    const matchingAttendee = item.attendees.find((att) => att.id === attendee.id);
                                    expenseAmount = parseFloat(matchingAttendee?.business_expense_amount) || 0;
                                } else if (item.attendee_id === attendee.id) {
                                    // For supplies, incidentals, shopping, transportation
                                    expenseAmount = parseFloat(item.business_expense_amount) || 0;
                                }

                                if (!totalExpensesByCurrency[currency]) {
                                    totalExpensesByCurrency[currency] = 0;
                                }

                                totalExpensesByCurrency[currency] += expenseAmount;
                            });
                        });

                        const eventLimit = remainingLimits.eventTotal;
                        const dailyLimit = remainingLimits.dailyTotal || null;
                        const categoryLimits = remainingLimits.categories;
                        const individualLimits = remainingLimits.individual[attendee.id] || {};

                        let eventRemaining = eventLimit;
                        let dailyRemaining = dailyLimit;
                        let categoryRemaining = { ...categoryLimits };
                        let individualRemaining = { ...individualLimits };

                        let dailyExpenseTracker = {};

                        return (
                            <div key={attendee.id} style={{ marginBottom: "20px" }}>
                                {/* Attendee Name & Total grouped by currency */}
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="h6">
                                        {capitalizeFirstLetter(attendee.fname)} {capitalizeFirstLetter(attendee.lname)}
                                    </Typography>
                                    <Box textAlign="right">
                                        {Object.entries(totalExpensesByCurrency).map(([currency, total]) => (
                                            <Typography key={currency} variant="h6" color="primary">
                                                {Number(total).toFixed(2).toLocaleString()} {currency}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                                <Divider style={{ marginBottom: "10px" }} />

                                {Object.entries(attendeeExpenses).map(([category, items]) => {
                                    if (items.length === 0) return null;

                                    // Calculate totals per currency for this category
                                    const categoryTotalsByCurrency = {};
                                    items.forEach((item) => {
                                        let expenseAmount = 0;
                                        const currency = item.currency || "USD";

                                        if (Array.isArray(item.attendees)) {
                                            // For lodging, meals, activities, flights
                                            const matchingAttendee = item.attendees.find((att) => att.id === attendee.id);
                                            expenseAmount = parseFloat(matchingAttendee?.business_expense_amount) || 0;
                                        } else if (item.attendee_id === attendee.id) {
                                            // For receipts: supplies, incidentals, shopping, transportation
                                            expenseAmount = parseFloat(item.business_expense_amount) || 0;
                                        }

                                        if (!categoryTotalsByCurrency[currency]) {
                                            categoryTotalsByCurrency[currency] = 0;
                                        }

                                        categoryTotalsByCurrency[currency] += expenseAmount;
                                    });

                                    return (
                                        <div key={category} style={{ marginBottom: "15px" }}>
                                            {/* Category Name & Total grouped by currency */}
                                            {Object.entries(categoryTotalsByCurrency).map(([currency, total]) => (
                                                <Box key={currency} display="flex" justifyContent="space-between" fontWeight="bold">
                                                    <Typography variant="subtitle1" color="primary">
                                                        {category.charAt(0).toUpperCase() + category.slice(1)}
                                                    </Typography>
                                                    <Typography variant="subtitle1" color="primary">
                                                        {Number(total || 0).toFixed(2).toLocaleString()} {currency}
                                                    </Typography>
                                                </Box>
                                            ))}

                                            {/* List of Expenses (Light Gray) */}
                                            <List dense style={{ backgroundColor: "#f8f8f8", padding: "5px 0 5px 10px", borderRadius: "5px" }}>
                                                {items.map((item) => {
                                                    let expense = null;

                                                    if (Array.isArray(item.attendees)) {
                                                        // Lodging, meals, activities, flights
                                                        expense = item.attendees.find((att) => att.id === attendee.id);
                                                    } else if (item.attendee_id === attendee.id) {
                                                        // Supplies, shopping, incidentals, transportation
                                                        expense = item;
                                                    }

                                                    if (!expense || !expense.is_business_expense) return null;
                                                    const uniqueKey = `${attendee.id}-${item.id}`; // Unique key per attendee-item pair
                                                    const isExpanded = expandedItems[uniqueKey] || false;

                                                    let expenseAmount = parseFloat(expense.business_expense_amount) || 0;
                                                    let authorizedFromIndividual = 0,
                                                        authorizedFromDaily = 0,
                                                        authorizedFromCategory = 0,
                                                        authorizedFromEvent = 0;

                                                    // **Determine number of days/nights**
                                                    let numDays = item.time_table?.dates?.length || item.time_table?.days?.length || 1;
                                                    let perDayCost = expenseAmount / numDays;

                                                    // ✅ Ensure dailyRemaining is an object before assigning properties
                                                    if (typeof dailyRemaining !== "object" || dailyRemaining === null) {
                                                        dailyRemaining = {}; // Reinitialize as an empty object if it's not already
                                                    }
                                                    // Ensure categoryRemaining is an object per day
                                                    if (typeof categoryRemaining[category] !== "object" || categoryRemaining[category] === null) {
                                                        categoryRemaining[category] = {};
                                                    }

                                                    // ✅ Track expenses per day using dates (preferred) or days fallback
                                                    let dateKeys = [];

                                                    if (item.time_table?.dates?.length > 0) {
                                                        dateKeys = item.time_table.dates.map(dateObj => dateObj.date);
                                                    } else if (item.time_table?.days?.length > 0) {
                                                        dateKeys = item.time_table.days.map(dayObj => Object.keys(dayObj)[0]); // e.g., "day 1"
                                                    } else {
                                                        dateKeys = ["day 1"]; // Default fallback if no dates or days exist
                                                    }

                                                    dateKeys.forEach((dateKey) => {
                                                        if (!dailyRemaining[dateKey]) {
                                                            dailyRemaining[dateKey] = remainingLimits.dailyTotal; // ✅ Assign daily limit per day
                                                        }
                                                        if (!categoryRemaining[category][dateKey]) {
                                                            categoryRemaining[category][dateKey] = remainingLimits['categories'][category] || 0;
                                                        }
                                                    });


                                                    // ✅ **Step 1: Deduct Lodging Cost from Daily Limit**
                                                    if (category === "lodging" && (dailyLimit !== null || remainingLimits['categories'].lodging)) {

                                                        dateKeys.forEach((dateKey) => {

                                                            let availableDailyOrCategoryLimit = dailyRemaining[dateKey] || remainingLimits['categories'].lodging || 0;

                                                            if (availableDailyOrCategoryLimit > 0) {
                                                                let maxAllowable = Math.min(perDayCost, availableDailyOrCategoryLimit);

                                                                if (remainingLimits['categories'].lodging){
                                                                    authorizedFromCategory += maxAllowable;
                                                                } else if (dailyLimit !== null){
                                                                    authorizedFromDaily += maxAllowable;
                                                                    dailyRemaining[dateKey] -= maxAllowable; // ✅ **Reduce the daily limit properly**
                                                                }

                                                            }
                                                        });
                                                        if (remainingLimits['categories'].lodging) {
                                                            expenseAmount -= authorizedFromCategory; // ✅ **Reduce the expenseAmount**
                                                        }  else if (dailyLimit !== null) {
                                                            expenseAmount -= authorizedFromDaily; // ✅ **Reduce the expenseAmount**
                                                        }

                                                    }

                                                    // ✅ **Step 2: Deduct from Daily Limit for Non-Lodging Expenses**
                                                    if (expenseAmount > 0 && dailyLimit !== null) {
                                                        let totalAuthorizedFromDaily = 0;
                                                        dateKeys.forEach((dateKey) => {
                                                            let availableDailyLimit = dailyRemaining[dateKey] || 0;

                                                            if (availableDailyLimit > 0) {
                                                                let maxAllowable = Math.min(expenseAmount, availableDailyLimit);
                                                                totalAuthorizedFromDaily += maxAllowable;
                                                                dailyRemaining[dateKey] -= maxAllowable; // ✅ **Update dailyRemaining**
                                                            }
                                                        });
                                                        authorizedFromDaily += totalAuthorizedFromDaily;
                                                        expenseAmount -= totalAuthorizedFromDaily;
                                                    }

                                                    // ✅ **Step 3: Deduct from Individual Limit**
                                                    if (expenseAmount > 0 && individualRemaining[category]) {
                                                        const maxAllowable = Math.min(expenseAmount, individualRemaining[category]);
                                                        authorizedFromIndividual = maxAllowable;
                                                        individualRemaining[category] -= maxAllowable;
                                                        expenseAmount -= maxAllowable;
                                                    }

                                                    // ✅ **Step 4: Deduct from Category Limit**
                                                    if (expenseAmount > 0 && categoryRemaining[category]) {
                                                        let totalAuthorizedFromCategory = 0;

                                                        dateKeys.forEach((dateKey) => {
                                                            let availableCategoryLimit = categoryRemaining[category][dateKey] || 0; // Get per-day category limit

                                                            if (availableCategoryLimit > 0) {
                                                                let maxAllowable = Math.min(expenseAmount, availableCategoryLimit);
                                                                totalAuthorizedFromCategory += maxAllowable;
                                                                categoryRemaining[category][dateKey] -= maxAllowable; // ✅ Deduct per day
                                                                expenseAmount -= maxAllowable;
                                                            }
                                                        });

                                                        authorizedFromCategory += totalAuthorizedFromCategory;
                                                    }
                                                    // ✅ **Step 5: Deduct from Event-Wide Limit**
                                                    if (expenseAmount > 0 && eventRemaining > 0) {
                                                        const maxAllowable = Math.min(expenseAmount, eventRemaining);
                                                        authorizedFromEvent = maxAllowable;
                                                        eventRemaining -= maxAllowable;
                                                        expenseAmount -= maxAllowable;
                                                    }

                                                    const totalAuthorized = authorizedFromIndividual + authorizedFromDaily + authorizedFromCategory + authorizedFromEvent;
                                                    const remainingAmount = Math.max((expense.business_expense_amount || 0) - totalAuthorized, 0);

                                                    return (
                                                        <ListItem key={uniqueKey} alignItems="flex-start" sx={{paddingRight:0}}>
                                                            {/* Primary Section with Toggle Button */}
                                                            <ListItemText
                                                                primary={
                                                                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                                                        <Typography variant="body2">
                                                                            {
                                                                                category === 'meals' ? item.title || item.name : item.name || item.title
                                                                            }
                                                                            {item.notes}
                                                                        </Typography>
                                                                        <Box display="flex" alignItems="center">
                                                                            {
                                                                                remainingAmount > 0 ?
                                                                                    remainingAmount === parseFloat(expense.business_expense_amount) || 0 ?
                                                                                        <i className="icon-warning"
                                                                                           style={{
                                                                                               fontSize: '11px',
                                                                                               color: 'red',
                                                                                               border: '1px solid #428ce2',
                                                                                               borderRadius: '20px',
                                                                                               padding: '5px',
                                                                                           }}
                                                                                        />
                                                                                        :
                                                                                        <i className="icon-warning"
                                                                                           style={{
                                                                                               fontSize: '11px',
                                                                                               color: 'orange',
                                                                                               border: '1px solid #428ce2',
                                                                                               borderRadius: '20px',
                                                                                               padding: '5px',
                                                                                           }}
                                                                                        />
                                                                                    :
                                                                                    <i className="icon-check"
                                                                                       style={{
                                                                                           fontSize: '11px',
                                                                                           color: 'green',
                                                                                           border: '1px solid #428ce2',
                                                                                           borderRadius: '20px',
                                                                                           padding: '5px',
                                                                                       }}/>
                                                                            }
                                                                            <Typography variant="body2" sx={{marginLeft:'10px'}}>
                                                                                <strong>
                                                                                    {Number(expense?.business_expense_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.currency || "USD"}
                                                                                </strong>
                                                                            </Typography>
                                                                            <IconButton size="small" onClick={() => toggleExpand(attendee.id, item.id)} sx={{ marginLeft: "8px" }}>
                                                                                {isExpanded ?
                                                                                    <i className="icon-chevron-up" style={{fontSize:'14px'}}/> :
                                                                                    <i className="icon-chevron-down" style={{fontSize:'14px'}}/>}
                                                                            </IconButton>

                                                                        </Box>
                                                                    </Box>
                                                                }
                                                                secondary={isExpanded && (
                                                                    <Box textAlign="right" sx={{ marginTop: "8px", width: "100%" }}>
                                                                        <Typography variant="body2" color="textPrimary" fontWeight="bold">
                                                                            Auth & Payment
                                                                        </Typography>
                                                                        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>

                                                                            {authorizedFromIndividual > 0 && (
                                                                                <Typography variant="body2" color="textSecondary">
                                                                                    Individual Authorization:&nbsp;&nbsp;
                                                                                    <strong>
                                                                                        {Number(authorizedFromIndividual || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.currency || "USD"}
                                                                                    </strong>
                                                                                </Typography>
                                                                            )}
                                                                            {authorizedFromDaily > 0 && (
                                                                                <Typography variant="body2" color="textSecondary">
                                                                                    Daily Authorization:&nbsp;&nbsp;
                                                                                    <strong>
                                                                                        {Number(authorizedFromDaily || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.currency || "USD"}
                                                                                    </strong>
                                                                                </Typography>
                                                                            )}
                                                                            {authorizedFromCategory > 0 && (
                                                                                <Typography variant="body2" color="textSecondary">
                                                                                    Category Authorization:&nbsp;&nbsp;
                                                                                    <strong>
                                                                                        {Number(authorizedFromCategory || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.currency || "USD"}
                                                                                    </strong>
                                                                                </Typography>
                                                                            )}
                                                                            {authorizedFromEvent > 0 && (
                                                                                <Typography variant="body2" color="textSecondary">
                                                                                    Event Authorization:&nbsp;&nbsp;
                                                                                    <strong>
                                                                                        {Number(authorizedFromEvent || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.currency || "USD"}
                                                                                    </strong>
                                                                                </Typography>
                                                                            )}

                                                                            {/* Total Covered */}
                                                                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: "bold" }}>
                                                                                Total Covered:&nbsp;&nbsp;
                                                                                <strong>
                                                                                    {Number(totalAuthorized || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.currency || "USD"}
                                                                                </strong>
                                                                            </Typography>

                                                                            {/* Remaining Out-of-Pocket Amount */}
                                                                            {expense.business_expense_amount > 0 && (
                                                                                <Typography variant="body2" color={remainingAmount > 0 ? "error" : "success"} sx={{ fontWeight: "bold" }}>
                                                                                    {remainingAmount > 0
                                                                                        ? `Not Covered: ${Number(remainingAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${item.currency || "USD"}`
                                                                                        : "Fully Covered"
                                                                                    }
                                                                                </Typography>
                                                                            )
                                                                            }

                                                                            {/* Reimbursed Amount */}
                                                                            <Typography variant="body2" color="textSecondary">
                                                                                Reimbursed:&nbsp;&nbsp;
                                                                                {Number(expense.paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.currency || "USD"}
                                                                            </Typography>

                                                                            <Typography variant="body2" color="textSecondary">
                                                                                Authorized By: {expense.business_expense_amount_authorized_by || "N/A"}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary">
                                                                                Reference ID: {expense.business_expense_reference_id || "N/A"}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="textSecondary">
                                                                                Authorization Date: {formatDate(expense.business_expense_amount_authorized_timestamp)}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                            />
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </div>
                                    );
                                })}

                                {/* Submit Report For Review & Token Tracking Section */}
                                <Box
                                    sx={{
                                        mt: 3,
                                        p: 2.5,
                                        backgroundColor: "#f8fafc",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: "8px"
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1e293b", mb: 1.5 }}>
                                        Submit Report for Review
                                    </Typography>

                                    {/* Dispatch Form Inputs */}
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        <TextField
                                            label="Recipient Email"
                                            variant="outlined"
                                            type="email"
                                            size="small"
                                            fullWidth
                                            placeholder="reviewer@company.com"
                                            value={recipientEmails[attendee.id] || ""}
                                            onChange={(e) =>
                                                setRecipientEmails({ ...recipientEmails, [attendee.id]: e.target.value })
                                            }
                                            sx={{ backgroundColor: "#ffffff" }}
                                        />

                                        <TextField
                                            label="Message / Notes for Reviewer (Optional)"
                                            variant="outlined"
                                            multiline
                                            rows={2}
                                            size="small"
                                            fullWidth
                                            placeholder="Add context or notes regarding out-of-pocket expenses..."
                                            value={messageForRecipients[attendee.id] || ""}
                                            onChange={(e) =>
                                                setMessageForRecipients({ ...messageForRecipients, [attendee.id]: e.target.value })
                                            }
                                            sx={{ backgroundColor: "#ffffff" }}
                                        />

                                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                                            <Typography variant="caption" color="textSecondary">
                                                Reviewers receive an authorization link valid for 7 days.
                                            </Typography>

                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() => handleSendReportForReview(attendee)}
                                                disabled={!recipientEmails[attendee.id]}
                                                sx={{ textTransform: "none", px: 3, fontWeight: 600 }}
                                            >
                                                Send Review Request
                                            </Button>
                                        </Box>
                                    </Box>

                                    {/* 🌟 REVIEW ACCESS TOKENS & REQUEST HISTORY LIST */}
                                    {(() => {
                                        const tokensList = reviewTokens.filter(
                                            (token) => !token.attendee_id || token.attendee_id === attendee.id
                                        );

                                        if (tokensList.length === 0) return null;

                                        return (
                                            <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e2e8f0" }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#334155", mb: 1.5 }}>
                                                    Submitted Review Requests ({tokensList.length})
                                                </Typography>

                                                <List disablePadding>
                                                    {tokensList.map((tokenObj) => {
                                                        console.log("tokenObj : ", tokenObj);
                                                        const isThreadOpen = activeThreadTokenId === tokenObj.token;
                                                        const messages = tokenObj.messages || [];
                                                        const isRevoked = tokenObj.status === "REVOKED";
                                                        const isExpired = tokenObj.status === "EXPIRED";

                                                        console.log("isRevoked : ", isRevoked);
                                                        console.log("isExpired : ", isExpired);

                                                        return (
                                                            <Paper
                                                                key={tokenObj.token}
                                                                elevation={0}
                                                                sx={{
                                                                    mb: 2,
                                                                    p: 2,
                                                                    backgroundColor: isRevoked || isExpired ? "#fafafa" : "#ffffff",
                                                                    border: "1px solid",
                                                                    borderColor: isThreadOpen ? "#3b82f6" : "#e2e8f0",
                                                                    borderRadius: "12px",
                                                                    transition: "all 0.2s ease-in-out",
                                                                    boxShadow: isThreadOpen ? "0 4px 20px rgba(59, 130, 246, 0.08)" : "none",
                                                                    "&:hover": {
                                                                        borderColor: isRevoked || isExpired ? "#cbd5e1" : "#93c5fd",
                                                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.03)"
                                                                    }
                                                                }}
                                                            >
                                                                {/* Request Header Row */}
                                                                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
                                                                    <Box display="flex" alignItems="center" gap={1.5}>
                                                                        {/* Avatar Badge */}
                                                                        <Avatar
                                                                            sx={{
                                                                                width: 38,
                                                                                height: 38,
                                                                                bgcolor: isRevoked || isExpired ? "#cbd5e1" : "#eff6ff",
                                                                                color: isRevoked || isExpired ? "#64748b" : "#2563eb",
                                                                                fontSize: "0.875rem",
                                                                                fontWeight: 700,
                                                                                border: "1px solid",
                                                                                borderColor: isRevoked || isExpired ? "#e2e8f0" : "#bfdbfe"
                                                                            }}
                                                                        >
                                                                            {tokenObj.reviewer_email?.charAt(0).toUpperCase() || "R"}
                                                                        </Avatar>

                                                                        <Box>
                                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                                                                                {tokenObj.reviewer_email}
                                                                            </Typography>

                                                                            <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.2 }}>
                                                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
                                                                                    Sent {formatDate(tokenObj.created_at || tokenObj.timestamp)}
                                                                                </Typography>

                                                                                {tokenObj.token && (
                                                                                    <>
                                                                                        <Typography variant="caption" sx={{ color: "#cbd5e1" }}>•</Typography>
                                                                                        <Tooltip title="Click to copy raw access token" arrow placement="top">
                                                                                            <Chip
                                                                                                label={`Token: ${tokenObj.token.slice(0, 8)}...`}
                                                                                                size="small"
                                                                                                onClick={() => navigator.clipboard.writeText(tokenObj.token)}
                                                                                                sx={{
                                                                                                    fontFamily: "monospace",
                                                                                                    fontSize: "0.68rem",
                                                                                                    height: "20px",
                                                                                                    backgroundColor: "#f1f5f9",
                                                                                                    color: "#475569",
                                                                                                    cursor: "pointer",
                                                                                                    fontWeight: 600,
                                                                                                    "&:hover": { backgroundColor: "#e2e8f0" }
                                                                                                }}
                                                                                            />
                                                                                        </Tooltip>
                                                                                    </>
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>

                                                                    {/* Status & Action Buttons */}
                                                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                                        {renderStatusBadge(tokenObj.status)}

                                                                        {/* Action: Resend Email */}
                                                                        {!isRevoked && !isExpired && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="text"
                                                                                disabled={sendingEmail}
                                                                                onClick={() => handleResendReviewEmail(tokenObj)}
                                                                                sx={{
                                                                                    textTransform: "none",
                                                                                    fontSize: "0.75rem",
                                                                                    fontWeight: 600,
                                                                                    color: "#2563eb",
                                                                                    borderRadius: "8px",
                                                                                    px: 1.5,
                                                                                    "&:hover": { backgroundColor: "#eff6ff" }
                                                                                }}
                                                                            >
                                                                                ✉️ Resend Email
                                                                            </Button>
                                                                        )}

                                                                        {/* Action: Dynamic Revoke / Restore Toggle */}
                                                                        {!isExpired && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="text"
                                                                                color={isRevoked ? "success" : "error"}
                                                                                onClick={() => handleRevokeToken(tokenObj)}
                                                                                sx={{
                                                                                    textTransform: "none",
                                                                                    fontSize: "0.75rem",
                                                                                    fontWeight: 600,
                                                                                    borderRadius: "8px",
                                                                                    px: 1.5,
                                                                                    "&:hover": {
                                                                                        backgroundColor: isRevoked ? "#f0fdf4" : "#fef2f2"
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {isRevoked ? "🔄 Restore Access" : "🚫 Revoke"}
                                                                            </Button>
                                                                        )}

                                                                        {/* Toggle Thread / Messages */}
                                                                        <Button
                                                                            size="small"
                                                                            variant={isThreadOpen ? "contained" : "outlined"}
                                                                            disableElevation
                                                                            onClick={() => setActiveThreadTokenId(isThreadOpen ? null : tokenObj.token)}
                                                                            sx={{
                                                                                textTransform: "none",
                                                                                fontSize: "0.75rem",
                                                                                fontWeight: 600,
                                                                                borderRadius: "8px",
                                                                                px: 1.5,
                                                                                borderColor: "#cbd5e1",
                                                                                backgroundColor: isThreadOpen ? "#1e293b" : "transparent",
                                                                                color: isThreadOpen ? "#ffffff" : "#475569",
                                                                                "&:hover": {
                                                                                    backgroundColor: isThreadOpen ? "#0f172a" : "#f8fafc",
                                                                                    borderColor: "#94a3b8"
                                                                                }
                                                                            }}
                                                                        >
                                                                            💬 Messages ({messages.length})
                                                                        </Button>
                                                                    </Box>
                                                                </Box>

                                                                {/* Initial Note Banner */}
                                                                {(tokenObj.initial_note || tokenObj.message) && (
                                                                    <Box
                                                                        sx={{
                                                                            mt: 1.5,
                                                                            p: 1.25,
                                                                            backgroundColor: "#f8fafc",
                                                                            borderRadius: "8px",
                                                                            borderLeft: "4px solid #3b82f6",
                                                                            display: "flex",
                                                                            alignItems: "flex-start",
                                                                            gap: 1
                                                                        }}
                                                                    >
                                                                        <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#334155", fontStyle: "italic" }}>
                                                                            <strong style={{ fontStyle: "normal", color: "#1e293b" }}>Initial Note:</strong> "
                                                                            {tokenObj.initial_note || tokenObj.message}"
                                                                        </Typography>
                                                                    </Box>
                                                                )}

                                                                {/* Collapsible Messages Thread */}
                                                                {isThreadOpen && (
                                                                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed #e2e8f0" }}>
                                                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                                                                Discussion Thread
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                                                                {messages.length} {messages.length === 1 ? "entry" : "entries"}
                                                                            </Typography>
                                                                        </Box>

                                                                        <Box
                                                                            sx={{
                                                                                maxHeight: "200px",
                                                                                overflowY: "auto",
                                                                                my: 1,
                                                                                pr: 0.5,
                                                                                display: "flex",
                                                                                flexDirection: "column",
                                                                                gap: 1
                                                                            }}
                                                                        >
                                                                            {messages.length === 0 ? (
                                                                                <Box textAlign="center" py={2} sx={{ backgroundColor: "#f8fafc", borderRadius: "8px" }}>
                                                                                    <Typography variant="caption" color="textSecondary">
                                                                                        No comments posted yet. Send a note below to start the conversation! 💬
                                                                                    </Typography>
                                                                                </Box>
                                                                            ) : (
                                                                                messages.map((msg, idx) => {
                                                                                    const isAttendee = msg.sender === "ATTENDEE";
                                                                                    return (
                                                                                        <Box
                                                                                            key={msg.id || idx}
                                                                                            sx={{
                                                                                                p: 1.25,
                                                                                                borderRadius: "8px",
                                                                                                backgroundColor: isAttendee ? "#eff6ff" : "#f0fdf4",
                                                                                                border: "1px solid",
                                                                                                borderColor: isAttendee ? "#bfdbfe" : "#bbf7d0",
                                                                                                alignSelf: isAttendee ? "flex-end" : "flex-start",
                                                                                                maxWidth: "90%"
                                                                                            }}
                                                                                        >
                                                                                            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={0.3}>
                                                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: isAttendee ? "#1d4ed8" : "#15803d" }}>
                                                                                                    {isAttendee ? "You (Attendee)" : "Reviewer"}
                                                                                                </Typography>
                                                                                                <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem" }}>
                                                                                                    {formatDate(msg.timestamp)}
                                                                                                </Typography>
                                                                                            </Box>
                                                                                            <Typography variant="body2" sx={{ fontSize: "0.825rem", color: "#0f172a", lineHeight: 1.4 }}>
                                                                                                {msg.text || msg.message}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    );
                                                                                })
                                                                            )}
                                                                        </Box>

                                                                        {/* Reply Box */}
                                                                        {!isRevoked && !isExpired ? (
                                                                            <Box display="flex" gap={1} mt={1.5}>
                                                                                <TextField
                                                                                    size="small"
                                                                                    fullWidth
                                                                                    placeholder="Type a response or note..."
                                                                                    value={newMessageText}
                                                                                    onChange={(e) => setNewMessageText(e.target.value)}
                                                                                    disabled={postingMessage}
                                                                                    sx={{
                                                                                        "& .MuiOutlinedInput-root": {
                                                                                            borderRadius: "8px",
                                                                                            backgroundColor: "#ffffff"
                                                                                        }
                                                                                    }}
                                                                                />
                                                                                <Button
                                                                                    variant="contained"
                                                                                    size="small"
                                                                                    onClick={() => handleAddMessage(tokenObj.token)}
                                                                                    disabled={postingMessage || !newMessageText.trim()}
                                                                                    sx={{
                                                                                        textTransform: "none",
                                                                                        fontWeight: 600,
                                                                                        borderRadius: "8px",
                                                                                        px: 2.5,
                                                                                        backgroundColor: "#2563eb",
                                                                                        whiteSpace: "nowrap",
                                                                                        "&:hover": { backgroundColor: "#1d4ed8" }
                                                                                    }}
                                                                                >
                                                                                    {postingMessage ? "Sending..." : "Send"}
                                                                                </Button>
                                                                            </Box>
                                                                        ) : (
                                                                            <Typography variant="caption" color="textSecondary" sx={{ fontStyle: "italic", display: "block", mt: 1 }}>
                                                                                {isRevoked
                                                                                    ? "This review access is currently revoked. Click 'Restore Access' above to resume messaging."
                                                                                    : "This link has expired."}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Paper>
                                                        );
                                                    })}
                                                </List>
                                            </Box>
                                        );
                                    })()}
                                </Box>

                                <Divider sx={{ my: 3 }} />

                            </div>
                        );
                    })
                )}
            </Box>
        </Box>
    );
}

export default UnifiedBudgetBusinessExpenseReport